import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, FORECASTLY_LOGO_LIGHT } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle } from "@phosphor-icons/react";

export default function AuthFinish() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setAuthFromToken } = useAuth();
  const sessionId = params.get("session_id");

  const [pollState, setPollState] = useState("checking"); // checking | paid | unpaid | error
  const [statusInfo, setStatusInfo] = useState(null); // full public-status payload
  const [form, setForm] = useState({ password: "", confirm: "", name: "", business_name: "" });
  const [busy, setBusy] = useState(false);

  // Poll Stripe via our backend until the payment lands. New customers can submit
  // their password as soon as we see payment_status=paid.
  useEffect(() => {
    if (!sessionId) {
      setPollState("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/billing/public-status/${sessionId}`);
        if (cancelled) return;
        if (res.data) setStatusInfo(res.data);
        if (res.data?.payment_status === "paid") {
          setPollState("paid");
          return;
        }
      } catch {}
      if (attempts >= 15) {
        if (!cancelled) setPollState("unpaid");
        return;
      }
      setTimeout(tick, 1500);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/finish`, {
        session_id: sessionId,
        password: form.password,
        name: form.name,
        business_name: form.business_name,
      });
      if (setAuthFromToken) setAuthFromToken(res.data.token, res.data.user);
      toast.success(
        res.data.already_existed
          ? "Welcome back — your plan is upgraded."
          : "Account created. Let's build your forecast.",
      );
      nav(res.data.already_existed ? "/dashboard" : "/onboarding", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete signup");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black/15">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-24">
          <Link to="/" className="flex items-center">
            <img
              src={FORECASTLY_LOGO_LIGHT}
              alt="forecastly"
              className="w-[220px] h-auto object-contain"
            />
          </Link>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-16">
        {pollState === "checking" && (
          <div className="card text-center" data-testid="auth-finish-checking">
            <div className="font-heading uppercase text-2xl">Almost there…</div>
            <p className="mt-3 text-black/70">Just confirming your payment with Stripe…</p>
            <div className="mt-6 inline-block animate-pulse text-3xl">£</div>
          </div>
        )}
        {pollState === "unpaid" && (
          <div className="card" data-testid="auth-finish-unpaid">
            <div className="font-heading uppercase text-2xl">Hmm — still processing</div>
            <p className="mt-3 text-black/75 leading-relaxed">
              Your payment hasn't cleared yet. This usually takes a few seconds. Refresh this page
              in a minute, or contact <strong>hello@profitmind.co.uk</strong> if it persists.
            </p>
          </div>
        )}
        {pollState === "error" && (
          <div className="card" data-testid="auth-finish-error">
            <div className="font-heading uppercase text-2xl">Something's not right</div>
            <p className="mt-3 text-black/75 leading-relaxed">
              We couldn't find your checkout session. If you've already paid, please email{" "}
              <strong>hello@profitmind.co.uk</strong> with your receipt and we'll sort it out.
            </p>
          </div>
        )}
        {pollState === "paid" && (
          <div data-testid="auth-finish-form-wrap">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={20} weight="fill" />
              <span className="font-heading uppercase text-sm tracking-wide">
                Payment received
              </span>
            </div>
            <h1 className="font-heading uppercase text-4xl tracking-wide leading-[1.05]">
              One last step.
            </h1>
            <p className="mt-3 text-black/75 leading-relaxed">
              Set a password so you can log back in any time. Your email's already linked from the
              payment — you'll use it to sign in.
            </p>
            {/* Iter38 — warm confirmation for referred signups so the friend
                immediately sees "yes, my extra 30 days are locked in". */}
            {statusInfo?.referrer_first_name && statusInfo?.referral_trial_bonus_days ? (
              <div
                className="mt-5 p-4 rounded-xl border-[1.5px] border-black bg-[#FFF7E5]"
                data-testid="auth-finish-referral-card"
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none pt-0.5" aria-hidden>🎁</span>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-heading text-black/70">
                      Your friend's gift is applied
                    </div>
                    <div className="font-semibold text-base mt-0.5">
                      {String(statusInfo.referrer_first_name).charAt(0).toUpperCase() +
                        String(statusInfo.referrer_first_name).slice(1)}{" "}
                      gifted you {statusInfo.referral_trial_bonus_days} extra days on us.
                    </div>
                    <p className="text-sm text-black/75 mt-1 leading-relaxed">
                      Your trial is extended — enjoy the extra runway. We'll send you a friendly
                      heads-up a few days before your first charge.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
            <form onSubmit={submit} className="card mt-6 flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Your name</label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="finish-name"
                  placeholder="Magdalena"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Business name (optional)</label>
                <input
                  className="input"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  data-testid="finish-business"
                  placeholder="ProfitMIND"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Choose a password</label>
                <input
                  className="input"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="finish-password"
                  minLength={6}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Confirm password</label>
                <input
                  className="input"
                  type="password"
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  data-testid="finish-confirm"
                  minLength={6}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn-primary self-start"
                disabled={busy}
                data-testid="finish-submit"
              >
                {busy ? "Creating your account…" : "Finish & start"}
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
